/** Set true to show PGA on Commercial Live again (Thu–Sun). */
export const SHOW_PGA_COMMERCIAL_LIVE = true;

/**
 * Golf futures are offered on Commercial Live Thu–Sun (tournament days).
 * Uses the client's local calendar day: 0=Sun … 4=Thu … 6=Sat.
 */
export function isGolfFuturesLiveWindow(date: Date = new Date()): boolean {
  if (!SHOW_PGA_COMMERCIAL_LIVE) return false;
  const day = date.getDay();
  return day === 0 || day >= 4;
}

export const GOLF_SPORT_NAME = "Golf";
export const PGA_LEAGUE_NAME = "PGA";
export const GOLF_PGA_LIVE_KEY = `${GOLF_SPORT_NAME}-${PGA_LEAGUE_NAME}-Live`;

export function isPgaLiveLeagueKey(leagueKey?: string | null): boolean {
    return leagueKey === GOLF_PGA_LIVE_KEY;
}

export function isPgaCommercialLiveSelection(
    sport?: string | null,
    leagueName?: string | null,
    marketKey?: string | null,
): boolean {
    return (
        marketKey === "Live" &&
        sport?.toLowerCase() === GOLF_SPORT_NAME.toLowerCase() &&
        leagueName?.toUpperCase() === PGA_LEAGUE_NAME
    );
}

export function isPgaCommercialLiveEntry(
    sport?: { name?: string | null } | null,
    league?: { name?: string | null } | null,
): boolean {
    return (
        sport?.name?.toLowerCase() === GOLF_SPORT_NAME.toLowerCase() &&
        league?.name?.toUpperCase() === PGA_LEAGUE_NAME
    );
}

export function isPgaStraightGame(game: {
    leagueKey?: string | null;
    sport?: { name?: string | null } | null;
    league?: { name?: string | null } | null;
}): boolean {
    if (isPgaLiveLeagueKey(game.leagueKey)) return true;
    return (
        game.sport?.name?.toLowerCase() === GOLF_SPORT_NAME.toLowerCase() &&
        game.league?.name?.toUpperCase() === PGA_LEAGUE_NAME
    );
}

export type GolfFutureTab = {
    name: string;
    selections: string[];
    leagueKey: string;
};

const TEAM_FUTURE_PATTERN =
    /\b(winning team|team winner|best ball|ryder cup|solheim cup|presidents cup|teams?)\b/i;

export function formatGolfTournamentName(
    game?: {
        name?: string | null;
        tournament?: { name?: string | null } | null;
    } | null
): string {
    const raw = String(game?.tournament?.name || game?.name || "").trim();
    return raw.replace(/\s+\d{4}$/, "");
}

export function stripGolfTournamentFromMarket(
    marketName?: string | null,
    tournamentName?: string | null
): string {
    let market = String(marketName ?? "").trim();
    if (!market) return market;

    const full = String(tournamentName ?? "").trim().replace(/\s+\d{4}$/, "");
    const short = full.replace(/\s+(championship|invitational|classic|open|tournament)$/i, "").trim();
    const tokens = [...new Set([full, short].filter(token => token.length >= 2))];

    for (const token of tokens) {
        const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        market = market.replace(new RegExp(`\\s*${escaped}\\s*`, "ig"), " ").trim();
    }

    return market.replace(/\s{2,}/g, " ").trim();
}

export function isGolfTeamFuture(
    game?: {
        name?: string | null;
        future_type?: string | null;
        tournament?: { name?: string | null } | null;
        odds?: Array<{
            market?: string | null;
        }> | null;
    } | null,
    tabName?: string | null
): boolean {
    const text = [
        tabName,
        game?.future_type,
        game?.name,
        game?.tournament?.name,
        game?.odds?.[0]?.market,
    ]
        .filter(Boolean)
        .join(" ");

    return TEAM_FUTURE_PATTERN.test(text) && !/\bplayers?\b/i.test(text);
}

export function getGolfFutureSubjectLabel(
    game?: Parameters<typeof isGolfTeamFuture>[0],
    tabName?: string | null
): "TEAM" | "PLAYER" {
    return isGolfTeamFuture(game, tabName) ? "TEAM" : "PLAYER";
}

function normalizeFutureSelections(selections: unknown): string[] {
    if (Array.isArray(selections)) {
        return selections.map(String).map(s => s.trim()).filter(Boolean);
    }
    if (typeof selections === "string") {
        return selections.includes(",")
            ? selections.split(",").map(s => s.trim()).filter(Boolean)
            : (selections.trim() ? [selections.trim()] : []);
    }
    return [];
}

export function getPgaFutureTabs(sports?: Array<{
    name?: string | null;
    id?: string | null;
    leagues?: Array<{
        name?: string | null;
        futureCategories?: Array<{ items?: unknown } | null> | null;
    } | null> | null;
} | null> | null): GolfFutureTab[] {
    const golfSport =
        sports?.find(s => s?.name?.toLowerCase() === GOLF_SPORT_NAME.toLowerCase()) ??
        sports?.find(s => s?.id?.toLowerCase() === "golf");
    const pgaLeague = golfSport?.leagues?.find(
        l => l?.name?.toUpperCase() === PGA_LEAGUE_NAME
    );

    const tabs: GolfFutureTab[] = [];
    const seen = new Set<string>();

    for (const cat of pgaLeague?.futureCategories ?? []) {
        const rawItems = cat?.items;
        const items = Array.isArray(rawItems)
            ? rawItems
            : rawItems && typeof rawItems === "object"
                ? [rawItems]
                : [];

        for (const item of items as Array<{ name?: string; selections?: unknown; is_active?: boolean }>) {
            if (item?.is_active === false) continue;
            const selections = normalizeFutureSelections(item?.selections);
            if (selections.length === 0) continue;

            const leagueKey = `${GOLF_SPORT_NAME}-${PGA_LEAGUE_NAME}-${selections
                .join("_")
                .replace(/[, ]+/g, "_")}`;
            if (seen.has(leagueKey)) continue;
            seen.add(leagueKey);

            tabs.push({
                name: item?.name || selections.join(", "),
                selections,
                leagueKey,
            });
        }
    }

    return tabs;
}

function parseDateMs(value: unknown): number | null {
    if (value == null || value === "") return null;
    if (value instanceof Date) {
        const ms = value.getTime();
        return Number.isFinite(ms) ? ms : null;
    }
    if (typeof value === "object" && value !== null && "seconds" in value) {
        const seconds = Number((value as { seconds?: number }).seconds);
        return Number.isFinite(seconds) ? seconds * 1000 : null;
    }
    const ms = new Date(value as string | number).getTime();
    return Number.isFinite(ms) ? ms : null;
}

function normalizeStatus(value?: string | null): string {
    return String(value ?? "").toLowerCase().replace(/[\s_-]/g, "");
}

const LIVE_STATUSES = new Set(["live", "inprogress", "inplay"]);
const ENDED_STATUSES = new Set(["completed", "cancelled", "canceled", "closed", "settled", "final"]);

export function isFutureGameLive(game?: {
    is_live?: boolean | null;
    status?: string | null;
    start_date?: string | null;
    tournament?: {
        is_live?: boolean | null;
        status?: string | null;
        start_date?: unknown;
        end_date?: unknown;
    } | null;
} | null): boolean {
    if (!game) return false;
    if (game.is_live === true || game.tournament?.is_live === true) return true;

    const statuses = [game.status, game.tournament?.status].map(normalizeStatus);
    if (statuses.some(status => LIVE_STATUSES.has(status))) return true;
    if (statuses.some(status => ENDED_STATUSES.has(status))) return false;

    const start = parseDateMs(game.start_date) ?? parseDateMs(game.tournament?.start_date);
    if (start != null && start > Date.now()) return false;

    const end = parseDateMs(game.tournament?.end_date);
    if (end != null && end < Date.now()) return false;

    return start != null && start <= Date.now();
}

/** Hide golf futures from the prematch menu once start_date has passed. Other sports stay visible. */
export function hasFutureGameStarted(game?: {
    start_date?: string | null;
    sport?: { name?: string | null } | null;
    tournament?: { start_date?: unknown } | null;
} | null): boolean {
    const isGolf = game?.sport?.name?.toLowerCase() === GOLF_SPORT_NAME.toLowerCase();
    if (!isGolf) return false;
    const start = parseDateMs(game?.start_date) ?? parseDateMs(game?.tournament?.start_date);
    return start != null && start <= Date.now();
}
